import styled, { keyframes } from "styled-components";

import { media } from "../../styles/media";

const panelIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.99); }
  to { opacity: 1; transform: none; }
`;

const sheetIn = keyframes`
  from { transform: translateY(-8px); opacity: 0.4; }
  to { transform: none; opacity: 1; }
`;

const skeletonShine = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

export const NotificationPanelStyles = styled.div`
  .panelOverlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: transparent;
  }

  .panel {
    position: fixed;
    z-index: 1001;
    left: 248px;
    bottom: 84px;
    width: 400px;
    max-width: calc(100vw - 24px);
    max-height: calc(100vh - 120px);
    background: ${({ theme }) => theme.colors.semantic.container.white};
    border: 1px solid ${({ theme }) => theme.colors.semantic.separator};
    border-radius: ${({ theme }) => theme.radius[16]};
    box-shadow: 0px 8px 28px rgba(14, 12, 31, 0.16),
      0px 2px 6px rgba(14, 12, 31, 0.1), 0px 0px 0px 0.5px rgba(14, 12, 31, 0.04);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform-origin: bottom left;
    animation: ${panelIn} 0.16s cubic-bezier(0.2, 0.8, 0.3, 1);
  }

  .panelHead {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 16px 14px 20px;
    border-bottom: 1px solid #f1f1f5;

    .panelTitle {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.semantic.text.strong};
    }

    .spacer {
      flex: 1;
    }
  }

  .linkBtn {
    background: transparent;
    border: 0;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.accent[500]};
    padding: 6px 8px;
    border-radius: ${({ theme }) => theme.radius[8]};
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: ${({ theme }) => theme.colors.accent[50]};
    }

    &:disabled {
      color: ${({ theme }) => theme.colors.primary[300]};
      cursor: default;
      background: transparent;
    }
  }

  .iconBtn {
    width: 30px;
    height: 30px;
    border-radius: ${({ theme }) => theme.radius[8]};
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.colors.primary[500]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: ${({ theme }) => theme.colors.accent[50]};
      color: ${({ theme }) => theme.colors.accent[500]};
    }
  }

  .panelBody {
    overflow-y: auto;
    overflow-x: hidden;
    /* Floor above both the 4-row skeleton (~303px) and the empty/error state,
       so the panel doesn't resize when loading flips to a state — that jump
       reads as a flicker. */
    min-height: 320px;
    flex: 1;

    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.colors.primary[150]};
      border: 3px solid ${({ theme }) => theme.colors.semantic.container.white};
      border-radius: 8px;
    }
  }

  .panelFoot {
    border-top: 1px solid #f1f1f5;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: center;

    .linkBtn {
      color: ${({ theme }) => theme.colors.semantic.text.weak};

      &:hover {
        color: ${({ theme }) => theme.colors.accent[500]};
      }
    }
  }

  .listSub {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    padding: 14px 20px 6px;
    background: ${({ theme }) => theme.colors.semantic.container.white};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  /* ── notification item ── */
  .item {
    position: relative;
    display: flex;
    gap: 12px;
    padding: 12px 20px;
    cursor: pointer;
    transition: background 0.12s ease;
    align-items: flex-start;

    & + .item {
      border-top: 1px solid #f1f1f5;
    }

    &:hover {
      background: ${({ theme }) => theme.colors.accent[50]};
    }

    /* unseen = tint treatment */
    &.unseen {
      background: ${({ theme }) => theme.colors.accent[50]};

      &:hover {
        background: ${({ theme }) => theme.colors.accent[100]};
      }

      .itemTitle {
        font-weight: 600;
      }
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${({ theme }) => theme.colors.accent[500]};
      flex-shrink: 0;
      align-self: center;
      margin-left: 6px;
      visibility: hidden;
    }

    &.unseen .dot {
      visibility: visible;
    }
  }

  .lead {
    position: relative;
    flex-shrink: 0;

    .badgeCircle {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => theme.colors.accent[50]};
      color: ${({ theme }) => theme.colors.accent[500]};
    }

    .typeTag {
      position: absolute;
      right: -3px;
      bottom: -3px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${({ theme }) => theme.colors.semantic.accent.onAccent};
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.semantic.container.white};

      &.msg {
        background: ${({ theme }) => theme.colors.accent[400]};
      }

      &.cmt {
        background: ${({ theme }) => theme.colors.success[600]};
      }
    }
  }

  .itemBody {
    flex: 1;
    min-width: 0;

    .line1 {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }

    .itemTitle {
      font-size: 13.5px;
      font-weight: 500;
      line-height: 18px;
      color: ${({ theme }) => theme.colors.semantic.text.strong};
    }

    .time {
      margin-left: auto;
      flex-shrink: 0;
      font-size: 11px;
      line-height: 16px;
      color: ${({ theme }) => theme.colors.semantic.text.weak};
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    .desc {
      font-size: 12.5px;
      line-height: 17px;
      color: ${({ theme }) => theme.colors.primary[600]};
      margin-top: 2px;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;

      b {
        font-weight: 600;
        color: ${({ theme }) => theme.colors.semantic.text.strong};
      }
    }

    .meta {
      font-size: 11.5px;
      line-height: 16px;
      color: ${({ theme }) => theme.colors.semantic.text.weak};
      margin-top: 2px;
    }
  }

  /* ── seen-by stack ── */
  .seenBy {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-top: 8px;

    .eye {
      color: ${({ theme }) => theme.colors.primary[300]};
      display: flex;
    }

    .stack {
      display: flex;

      > * {
        margin-left: -6px;
        border: 1.5px solid ${({ theme }) => theme.colors.semantic.container.white};
        border-radius: 50%;
      }

      > *:first-child {
        margin-left: 0;
      }

      .more {
        width: 20px;
        height: 20px;
        background: ${({ theme }) => theme.colors.accent[100]};
        color: ${({ theme }) => theme.colors.accent[700]};
        font-size: 8px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  /* ── skeleton ── */
  .skRow {
    display: flex;
    gap: 12px;
    padding: 12px 20px;
    align-items: flex-start;

    & + .skRow {
      border-top: 1px solid #f1f1f5;
    }

    .skCol {
      flex: 1;
    }
  }

  .sk {
    background: linear-gradient(
      100deg,
      ${({ theme }) => theme.colors.primary[100]} 30%,
      #eeeeee 50%,
      ${({ theme }) => theme.colors.primary[100]} 70%
    );
    background-size: 200% 100%;
    animation: ${skeletonShine} 1.3s ease-in-out infinite;
    border-radius: 6px;
  }

  .skAv {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .skL1 {
    height: 12px;
    width: 62%;
  }

  .skL2 {
    height: 11px;
    width: 88%;
    margin-top: 9px;
  }

  .skL3 {
    height: 10px;
    width: 40%;
    margin-top: 9px;
  }

  /* ── empty / error states ── */
  .stateWrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 52px 28px;
    gap: 6px;

    .stateIcon {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: ${({ theme }) => theme.colors.accent[50]};
      color: ${({ theme }) => theme.colors.accent[400]};
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
    }

    &.isError .stateIcon {
      background: ${({ theme }) => theme.colors.error[50]};
      color: ${({ theme }) => theme.colors.error[500]};
    }

    .stateTitle {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.semantic.text.strong};
      margin: 0;
    }

    .stateSub {
      font-size: 13px;
      line-height: 19px;
      color: ${({ theme }) => theme.colors.semantic.text.weak};
      margin: 0;
      max-width: 280px;
    }

    .retryBtn {
      margin-top: 12px;
      height: 36px;
      padding: 0 16px;
      border-radius: ${({ theme }) => theme.radius[8]};
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0.1px;
      cursor: pointer;
      background: ${({ theme }) => theme.colors.semantic.container.white};
      color: ${({ theme }) => theme.colors.semantic.text.strong};
      border: 1px solid ${({ theme }) => theme.colors.semantic.separator};
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: background 0.15s, border-color 0.15s, color 0.15s;

      &:hover {
        background: ${({ theme }) => theme.colors.accent[50]};
        color: ${({ theme }) => theme.colors.accent[500]};
        border-color: ${({ theme }) => theme.colors.accent[500]};
      }
    }
  }

  .endRow {
    text-align: center;
    font-size: 11.5px;
    color: ${({ theme }) => theme.colors.primary[300]};
    padding: 16px;
    letter-spacing: 0.02em;
  }

  /* ── mobile: full-screen sheet under the top bar ── */
  ${({ theme }) => media.maxTablet`
    .panelOverlay {
      background: ${theme.colors.accent[900]}47;
      z-index: ${theme.zIndex.drawerBackdrop};
    }

    .panel {
      top: 56px;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      max-width: none;
      max-height: none;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      z-index: ${theme.zIndex.drawer};
      animation: ${sheetIn} 0.26s cubic-bezier(0.2, 0.8, 0.3, 1);
    }
  `}
`;
